import { createTheme } from '@mui/material/styles';

// By using module augmentation, we can add custom variables to the palette
// This allows for type safety when using these custom colors throughout the app.
declare module '@mui/material/styles/createPalette' {
  // Add custom shades to the standard PaletteColor object
  interface PaletteColor {
    extraLight?: string;
    extraDark?: string;
  }
  interface SimplePaletteColorOptions {
    extraLight?: string;
    extraDark?: string;
  }

  // Add custom shades to the background palette
  interface TypeBackground {
    light: string;
    base: string;
    dark: string;
    extraDark: string;
  }
}

// Define the color palette based on user's request
const theme = createTheme({
  palette: {
    primary: {
      extraLight: '#EEF2F7',
      light: '#DCE3EE',
      main: '#0E4096',      // Base
      dark: '#0B357A',
      extraDark: '#07275C',
    },
    secondary: {
      extraLight: '#E0F2F1',
      light: '#B2DFDB',
      main: '#26A69A',      // Base
      dark: '#00796B',
      extraDark: '#004D40',
    },
    error: {
      extraLight: '#FFEBEE',
      light: '#FFCDD2',
      main: '#E53935',      // Base
      dark: '#B71C1C',
      extraDark: '#7F0000',
    },
    warning: {
      extraLight: '#FFF8E1',
      light: '#FFECB3',
      main: '#FFA000',      // Base
      dark: '#FF6F00',
      extraDark: '#E65100',
    },
    info: {
      extraLight: '#E3F2FD',
      light: '#BBDEFB',
      main: '#1976D2',      // Base
      dark: '#0D47A1',
      extraDark: '#0B2F6D',
    },
    success: {
      extraLight: '#E8F5E9',
      light: '#C8E6C9',
      main: '#388E3C',      // Base
      dark: '#1B5E20',
      extraDark: '#0A3B0C',
    },
    text: {
      primary: '#212121',   // Base
      secondary: '#484848', // Light
      disabled: '#BDBDBD',  // Extra Light
    },
    background: {
      paper: '#FFFFFF',     // '#FBE9E7', // Extra Light
      default: '#F4F6F8',   // Light
      light: '#F4F6F8',
      base: '#E1E5E9',
      dark: '#CDD3D7',
      extraDark: '#AAB0B5',
    },
  },
  typography: {
    fontFamily: [
      'Trebuchet MS',
      'Trebuchet',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& label.Mui-focused': {
            color: '#26A69A', // secondary.main
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#26A69A', // secondary.main
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#26A69A', // secondary.main
              borderWidth: 2,
            },
            // Error state styling with warning color
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: '#FFA000', // warning.main
            },
            // Prevent blue background on autofill
            '& input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px white inset',
              WebkitTextFillColor: 'inherit',
              backgroundColor: 'background.paper',
            },
            // Style select inputs specifically
            '& .MuiSelect-select': {
              borderRadius: 8,
            },
          },
          // Error state label styling
          '& label.Mui-error': {
            color: 'rgba(0, 0, 0, 0.87)',
          },
          // Error state label styling when focused - green color
          // '& label.Mui-error.Mui-focused': {
          //   color: '#388E3C', // success.main (green)
          // },
          // Disabled state styling - maintain normal colors
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: 'rgba(0, 0, 0, 0.87) !important',
            color: 'rgba(0, 0, 0, 0.87) !important',
          },
          '& .MuiInputBase-input': {
            color: 'rgba(0, 0, 0, 0.87)',
          },
          '& .MuiInputLabel-root.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.87) !important',
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#26A69A', // secondary.main
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#26A69A', // secondary.main
              borderWidth: 2,
            },
            // Prevent blue background on autofill
            '& input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px white inset',
              WebkitTextFillColor: 'inherit',
              backgroundColor: 'background.paper',
            },
            // Style select inputs specifically
            '& .MuiSelect-select': {
              borderRadius: 8,
            },
            // Error state styling with warning color
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: '#FFA000', // warning.main
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#26A69A', // secondary.main
          },
          '&.Mui-error': {
            color: '#FFA000', // warning.main
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#E0F2F1', // secondary.extraLight
            '&:hover': {
              backgroundColor: '#E0F2F1', // secondary.extraLight
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            color: '#FFA000', // warning.main
          },
        },
      },
    },
  },
});

export default theme; 